import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookMarked, ShieldCheck, FileText, Compass, Bot } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STATUS_META } from "@/lib/visaStatus";

const PURPOSES = ["Tourism", "Business", "Transit", "Student", "Work"];

export default function Landing() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [nationality, setNationality] = useState("");
  const [destination, setDestination] = useState("");
  const [purpose, setPurpose] = useState("Tourism");
  const [duration, setDuration] = useState(14);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries));
  }, []);

  const handleCheck = async () => {
    if (!nationality || !destination) return;
    setLoading(true);
    try {
      const { data } = await api.post("/check-visa", {
        nationality,
        destination,
        purpose: purpose.toLowerCase(),
        duration_days: Number(duration) || 14,
      });
      navigate(`/results/${data.id}`);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: ShieldCheck,
      title: "Real-Time Visa Rules",
      body: "AI-parsed guidance across 190+ nationalities with source-linked disclaimers.",
    },
    {
      icon: FileText,
      title: "Premium PDF Reports",
      body: "Step-by-step application dossiers with document specs and embassy tips.",
    },
    {
      icon: Compass,
      title: "Trip Planner",
      body: "Save trips, track passport validity, and re-check requirements as rules change.",
    },
    {
      icon: Bot,
      title: "AI Travel Assistant",
      body: "24/7 chat for photo specs, transit rules, biometrics questions, and more.",
    },
  ];

  return (
    <div className="min-h-screen bg-grid">
      <div className="blob left-[-10%] top-[-10%] h-[360px] w-[360px] rounded-full bg-teal-400" />
      <div className="blob right-[-10%] top-[10%] h-[420px] w-[420px] rounded-full bg-indigo-400" />

      <Navbar />

      <main className="relative mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8 lg:pt-20">
        <section className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-8">
          <div className="fade-up">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
              </span>
              Live visa & document intelligence
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Know exactly what
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">documents you need</span>
                <span className="absolute inset-x-0 bottom-1 z-0 h-3 bg-teal-300/60" />
              </span>
              <br />
              before you fly.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              VisAura combines AI reasoning with global travel rules to tell you your exact
              visa status, required documents, embassy fees, and photo specs — instantly, for any
              nationality-to-destination pair.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <span
                  key={key}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${meta.className}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </span>
              ))}
            </div>
          </div>

          <div
            data-testid="hero-checker-card"
            className="fade-up rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-slate-900/5 sm:p-8"
          >
            <div className="mb-6 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                <BookMarked className="h-4 w-4" />
              </div>
              <h2 className="font-heading text-lg font-semibold">Instant visa checker</h2>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="nationality" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                  Your nationality
                </Label>
                <Select value={nationality} onValueChange={setNationality}>
                  <SelectTrigger data-testid="nationality-select" id="nationality" className="h-12">
                    <SelectValue placeholder="Select passport country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`nat-opt-${c.code}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="destination" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                  Destination
                </Label>
                <Select value={destination} onValueChange={setDestination}>
                  <SelectTrigger data-testid="destination-select" id="destination" className="h-12">
                    <SelectValue placeholder="Where are you going?" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`dest-opt-${c.code}`}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                  Travel purpose
                </Label>
                <div className="flex flex-wrap gap-2">
                  {PURPOSES.map((p) => (
                    <button
                      key={p}
                      data-testid={`purpose-${p.toLowerCase()}`}
                      type="button"
                      onClick={() => setPurpose(p)}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${
                        purpose === p
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="duration" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-500">
                  Duration (days)
                </Label>
                <Input
                  id="duration"
                  data-testid="duration-input"
                  type="number"
                  min={1}
                  max={365}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="h-12"
                />
              </div>

              <Button
                data-testid="hero-check-btn"
                onClick={handleCheck}
                disabled={loading || !nationality || !destination}
                className="h-12 w-full bg-slate-900 text-base font-semibold hover:bg-slate-800"
              >
                {loading ? "Analyzing..." : "Check my requirements"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-center text-xs text-slate-400">
                Free · No signup required · Powered by GPT-5.6 Terra
              </p>
            </div>
          </div>
        </section>

        <section className="mt-24 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-teal-500/60 hover:shadow-xl hover:shadow-teal-500/10"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-white transition-colors group-hover:bg-teal-600">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-24 rounded-3xl bg-slate-900 px-6 py-14 text-center text-white sm:px-12">
          <h2 className="font-heading text-3xl font-bold sm:text-4xl">
            Never miss a document again.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-300">
            Join thousands of travelers using VisAura to plan smarter and cross borders faster.
          </p>
          <Button
            data-testid="cta-get-started"
            onClick={() => navigate("/register")}
            className="mt-8 h-12 bg-teal-500 px-8 text-base font-semibold text-white hover:bg-teal-400"
          >
            Create free account
          </Button>
        </section>

        <footer className="mt-16 border-t border-slate-200 py-8 text-center text-xs text-slate-400">
          <p>
            © 2026 VisAura · Guidance is informational only. Always verify with the official
            embassy or consulate of your destination.
          </p>
        </footer>
      </main>
    </div>
  );
}
